import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, CheckCircle2, Trash2, Plus, Search, Printer, Info } from "lucide-react";
import { toast } from "sonner";

const F = ({ label, className = "", children }) => (
  <label className={`text-sm ${className}`}>
    <span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>
    {children}
  </label>
);

const emptyDraft = () => ({
  customer_id: "",
  franchise_branch_id: "",
  bill_to: "customer",         // 'customer' | 'franchise'
  warehouse_id: "",
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  payment_terms: "",
  reference: "",
  notes: "",
  invoice_discount_pct: 0,
  invoice_discount_amount: 0,
  lines: [],
});

export default function SalesInvoiceEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;

  const [form, setForm] = useState(emptyDraft());
  const [status, setStatus] = useState("draft");
  const [invoiceNo, setInvoiceNo] = useState(null);
  const [totals, setTotals] = useState(null);
  const [isIntrastate, setIsIntrastate] = useState(true);
  const [customerSnapshot, setCustomerSnapshot] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  // Product picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerRows, setPickerRows] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const pickerAbortRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load reference data + existing invoice
  useEffect(() => {
    (async () => {
      const [cs, ws, bs] = await Promise.all([
        api.get("/sales/customers", { params: { page: 1, page_size: 500 } }),
        api.get("/production/warehouses"),
        api.get("/sales/branches"),
      ]);
      setCustomers(cs.data?.items || []);
      setWarehouses(ws.data || []);
      setFranchises((bs.data || []).filter((b) => b.branch_type === "franchise" && b.is_active !== false));
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/sales/invoices/${id}`).then((r) => {
      const d = r.data;
      setStatus(d.status);
      setInvoiceNo(d.invoice_no);
      setTotals(d.totals);
      setIsIntrastate(d.is_intrastate);
      setCustomerSnapshot(d.customer_snapshot);
      const src = d.customer_snapshot?.source;
      const isFranchise = src === "franchise";
      setForm({
        customer_id: isFranchise ? "" : d.customer_id,
        franchise_branch_id: isFranchise ? (d.customer_snapshot?.branch_id || "") : "",
        bill_to: isFranchise ? "franchise" : "customer",
        warehouse_id: d.warehouse_id,
        invoice_date: d.invoice_date,
        due_date: d.due_date || "",
        payment_terms: d.payment_terms || "",
        reference: d.reference || "",
        notes: d.notes || "",
        invoice_discount_pct: d.invoice_discount_pct || 0,
        invoice_discount_amount: d.invoice_discount_amount || 0,
        lines: d.lines || [],
      });
    }).catch(() => toast.error("Failed to load invoice"));
  }, [id]);

  const readOnly = status !== "draft";

  const selectedCustomer = useMemo(() => customers.find((c) => c.id === form.customer_id), [customers, form.customer_id]);
  const selectedFranchise = useMemo(() => franchises.find((f) => f.id === form.franchise_branch_id), [franchises, form.franchise_branch_id]);
  const priceLevel = form.bill_to === "franchise"
    ? 3
    : (selectedCustomer?.price_level || customerSnapshot?.price_level || 2);

  // Product picker search
  const searchPicker = useCallback(async (q) => {
    if (!form.warehouse_id) { setPickerRows([]); return; }
    if (pickerAbortRef.current) pickerAbortRef.current.abort?.();
    const ctrl = new AbortController();
    pickerAbortRef.current = ctrl;
    setPickerLoading(true);
    try {
      const r = await api.get("/sales/warehouse-stock-picker", {
        params: { warehouse_id: form.warehouse_id, search: q, price_level: priceLevel, limit: 40 },
        signal: ctrl.signal,
      });
      setPickerRows(r.data || []);
    } catch (e) { if (e.name !== "CanceledError") toast.error("Search failed"); }
    finally { setPickerLoading(false); }
  }, [form.warehouse_id, priceLevel]);

  useEffect(() => {
    if (!pickerOpen) return;
    const t = setTimeout(() => searchPicker(pickerQuery), 200);
    return () => clearTimeout(t);
  }, [pickerQuery, pickerOpen, searchPicker]);

  const addLine = (row) => {
    if (form.lines.some((l) => l.product_id === row.product_id)) {
      toast.error("Product already added — bump the qty instead");
      return;
    }
    setForm((f) => ({
      ...f,
      lines: [
        ...f.lines,
        {
          product_id: row.product_id,
          product_name: row.product_name,
          product_sku: row.product_sku,
          hsn_code: row.hsn_code || "",
          qty: 1,
          rate: Number(row.rate) || 0,
          discount_pct: 0,
          tax_pct: Number(row.tax_pct) || 3,
          _avail_qty: row.qty,
        },
      ],
    }));
    setPickerQuery("");
    setPickerOpen(false);
  };

  const setLine = (idx, patch) => {
    setForm((f) => ({ ...f, lines: f.lines.map((ln, i) => i === idx ? { ...ln, ...patch } : ln) }));
  };
  const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  // Client-side preview totals (server is source-of-truth)
  const preview = useMemo(() => {
    let subtotal = 0, lineDisc = 0, netBefore = 0;
    const lines = form.lines.map((ln) => {
      const s = Number(ln.qty || 0) * Number(ln.rate || 0);
      const ld = s * (Number(ln.discount_pct || 0) / 100);
      const nb = s - ld;
      subtotal += s; lineDisc += ld; netBefore += nb;
      return { ...ln, _subtotal: s, _line_disc: ld, _net_before: nb };
    });
    let invDisc = netBefore * (Number(form.invoice_discount_pct || 0) / 100) + Number(form.invoice_discount_amount || 0);
    if (invDisc > netBefore) invDisc = netBefore;
    let cgst = 0, sgst = 0, igst = 0, taxable = 0;
    lines.forEach((ln) => {
      const share = netBefore > 0 ? ln._net_before / netBefore : 0;
      const tx = ln._net_before - (invDisc * share);
      const tax = tx * (Number(ln.tax_pct || 0) / 100);
      taxable += tx;
      if (isIntrastate) { cgst += tax / 2; sgst += tax / 2; } else { igst += tax; }
    });
    const taxTotal = cgst + sgst + igst;
    const pre = taxable + taxTotal;
    const grand = Math.round(pre);
    return {
      subtotal, lineDisc, invDisc, taxable, cgst, sgst, igst, taxTotal, grand, roundoff: grand - pre,
    };
  }, [form, isIntrastate]);

  const canSave = (form.bill_to === "customer" ? form.customer_id : form.franchise_branch_id) && form.warehouse_id && form.lines.length > 0;

  const save = async () => {
    if (!canSave) return toast.error("Bill-To, warehouse and at least one line are required");
    setSaving(true);
    try {
      const payload = {
        customer_id: form.bill_to === "customer" ? form.customer_id : null,
        franchise_branch_id: form.bill_to === "franchise" ? form.franchise_branch_id : null,
        warehouse_id: form.warehouse_id,
        invoice_date: form.invoice_date,
        due_date: form.due_date,
        payment_terms: form.payment_terms,
        reference: form.reference,
        notes: form.notes,
        invoice_discount_pct: Number(form.invoice_discount_pct) || 0,
        invoice_discount_amount: Number(form.invoice_discount_amount) || 0,
        lines: form.lines.map((ln) => ({
          product_id: ln.product_id,
          product_name: ln.product_name,
          product_sku: ln.product_sku,
          hsn_code: ln.hsn_code || null,
          qty: Number(ln.qty),
          rate: Number(ln.rate),
          discount_pct: Number(ln.discount_pct) || 0,
          tax_pct: Number(ln.tax_pct) || 0,
        })),
      };
      const r = isNew ? await api.post("/sales/invoices", payload) : await api.put(`/sales/invoices/${id}`, payload);
      toast.success(isNew ? "Draft saved" : "Draft updated");
      if (isNew) nav(`/sales/invoices/${r.data.id}/edit`);
      else {
        setTotals(r.data.totals);
        setIsIntrastate(r.data.is_intrastate);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    if (!id) return toast.error("Save the draft first");
    if (!window.confirm("Approve this invoice? This will allocate the invoice number and deduct stock from the warehouse.")) return;
    setApproving(true);
    try {
      const r = await api.post(`/sales/invoices/${id}/approve`);
      toast.success(`Approved as ${r.data.invoice_no}`);
      setStatus("approved");
      setInvoiceNo(r.data.invoice_no);
      setTotals(r.data.totals);
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setApproving(false); }
  };

  const cancel = async () => {
    if (!id) return;
    if (!window.confirm("Cancel this invoice? If approved, stock will be reversed.")) return;
    try {
      await api.post(`/sales/invoices/${id}/cancel`);
      toast.success("Cancelled");
      setStatus("cancelled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const t = totals || {
    subtotal: preview.subtotal, taxable_amount: preview.taxable,
    cgst_total: preview.cgst, sgst_total: preview.sgst, igst_total: preview.igst,
    tax_total: preview.taxTotal, roundoff: preview.roundoff, grand_total: preview.grand,
    invoice_discount: preview.invDisc, line_discount_total: preview.lineDisc,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => nav("/sales/invoices")} className="gap-1.5" data-testid="inv-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <h2 className="font-heading font-bold text-lg text-gray-900">
          {isNew ? "New Invoice" : (invoiceNo || `Draft #${id?.slice(0, 8)}`)}
        </h2>
        <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${status === "draft" ? "bg-gray-100 text-gray-700 border-gray-200" : status === "approved" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>{status}</span>
        <div className="ml-auto flex gap-2">
          {status === "approved" && (
            <a href={`/sales/invoices/${id}/print`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="gap-1.5" data-testid="inv-print-btn"><Printer className="h-4 w-4" /> Print</Button>
            </a>
          )}
          {status !== "cancelled" && !isNew && (
            <Button variant="outline" onClick={cancel} className="gap-1.5 text-red-700 border-red-200 hover:bg-red-50" data-testid="inv-cancel-btn">Cancel Invoice</Button>
          )}
          {status === "draft" && (
            <>
              <Button variant="outline" onClick={save} disabled={saving} className="gap-1.5" data-testid="inv-save-btn"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Draft"}</Button>
              {!isNew && (
                <Button className="bg-emerald-700 hover:bg-emerald-800 gap-1.5" onClick={approve} disabled={approving || !canSave} data-testid="inv-approve-btn"><CheckCircle2 className="h-4 w-4" /> {approving ? "Approving…" : "Approve & Lock"}</Button>
              )}
            </>
          )}
        </div>
      </div>

      <Surface>
        <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Header</h3>
        <div className="grid grid-cols-4 gap-4">
          <F label="Bill To *">
            <div className="flex gap-1 h-9 rounded-md border border-gray-200 p-0.5 bg-gray-50">
              <button
                type="button"
                onClick={() => !readOnly && setForm((f) => ({ ...f, bill_to: "customer", franchise_branch_id: "" }))}
                disabled={readOnly}
                data-testid="inv-billto-customer"
                className={`flex-1 rounded text-xs font-semibold transition-colors ${form.bill_to === "customer" ? "bg-white text-[#9B1C1C] shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
              >Customer</button>
              <button
                type="button"
                onClick={() => !readOnly && setForm((f) => ({ ...f, bill_to: "franchise", customer_id: "" }))}
                disabled={readOnly}
                data-testid="inv-billto-franchise"
                className={`flex-1 rounded text-xs font-semibold transition-colors ${form.bill_to === "franchise" ? "bg-white text-[#9B1C1C] shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
              >Franchise Store</button>
            </div>
          </F>
          {form.bill_to === "customer" ? (
            <F label="Customer *">
              <select value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" disabled={readOnly} data-testid="inv-customer">
                <option value="">— Select customer —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}{c.mobile ? ` (${c.mobile})` : ""}</option>)}
              </select>
              {selectedCustomer && (
                <div className="text-[11px] text-gray-500 mt-1">
                  {selectedCustomer.customer_type} · L{selectedCustomer.price_level} · {selectedCustomer.gstin || "no GSTIN"} · {selectedCustomer.state || "no state"}
                </div>
              )}
            </F>
          ) : (
            <F label="Franchise Store *">
              <select value={form.franchise_branch_id} onChange={(e) => set("franchise_branch_id", e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" disabled={readOnly} data-testid="inv-franchise">
                <option value="">— Select franchise —</option>
                {franchises.map((fr) => <option key={fr.id} value={fr.id}>🏪 {fr.code ? `${fr.code} · ` : ""}{fr.name}{fr.gstin ? ` (${fr.gstin.slice(0, 5)}…)` : ""}</option>)}
              </select>
              {selectedFranchise && (
                <div className="text-[11px] text-gray-500 mt-1">
                  Franchise · L3 pricing · {selectedFranchise.gstin || "no GSTIN"} · {selectedFranchise.state || "no state"}
                </div>
              )}
            </F>
          )}
          <F label="Warehouse *">
            <select value={form.warehouse_id} onChange={(e) => { set("warehouse_id", e.target.value); setForm((f) => ({ ...f, lines: [] })); }} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" disabled={readOnly} data-testid="inv-warehouse">
              <option value="">— Pick warehouse —</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            {form.lines.length > 0 && !readOnly && <div className="text-[11px] text-amber-600 mt-1">Switching warehouse clears the lines.</div>}
          </F>
          <F label="Invoice Date *">
            <Input type="date" value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} disabled={readOnly} data-testid="inv-date" />
          </F>
          <F label="Due Date">
            <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} disabled={readOnly} data-testid="inv-due" />
          </F>
          <F label="Reference (PO#/order#)">
            <Input value={form.reference} onChange={(e) => set("reference", e.target.value)} disabled={readOnly} data-testid="inv-ref" />
          </F>
          <F label="Payment Terms">
            <Input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} placeholder="e.g. Net 15 days" disabled={readOnly} data-testid="inv-terms" />
          </F>
          <F label="Notes" className="col-span-2">
            <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} disabled={readOnly} data-testid="inv-notes" />
          </F>
        </div>
      </Surface>

      <Surface padded={false}>
        <div className="p-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
          <h3 className="font-heading font-semibold text-sm">Line Items</h3>
          {!readOnly && (
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <Input
                value={pickerQuery}
                placeholder={form.warehouse_id ? "Search warehouse stock…" : "Pick a warehouse first to enable product search"}
                onFocus={() => setPickerOpen(true)}
                onChange={(e) => { setPickerQuery(e.target.value); setPickerOpen(true); }}
                onBlur={() => setTimeout(() => setPickerOpen(false), 200)}
                disabled={!form.warehouse_id}
                className="pl-8"
                data-testid="inv-picker-search"
              />
              {pickerOpen && form.warehouse_id && (
                <div className="absolute z-40 mt-1 left-0 right-0 max-h-80 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg" data-testid="inv-picker-panel">
                  {pickerLoading && <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>}
                  {!pickerLoading && pickerRows.length === 0 && <div className="px-3 py-3 text-xs text-gray-500">{pickerQuery ? "No matches." : "Type to search products in this warehouse."}</div>}
                  {pickerRows.map((r) => (
                    <button
                      key={r.product_id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => addLine(r)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50 text-left border-b border-gray-100 last:border-0"
                      data-testid={`inv-picker-row-${r.product_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.product_name}</div>
                        <div className="text-[11px] text-gray-500 font-mono">{r.product_sku || "—"}{r.hsn_code ? ` · HSN ${r.hsn_code}` : ""}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs tabular-nums text-gray-700">{formatINR(r.rate)}</div>
                        <div className="text-[10px] text-gray-500">Qty <span className="font-semibold tabular-nums">{r.qty}</span> · Tax {r.tax_pct}%</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <span className="text-xs text-gray-500 ml-auto">{form.lines.length} line(s) · {isIntrastate ? "Intrastate (CGST+SGST)" : "Interstate (IGST)"}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
              <th className="px-3 py-2 w-8">#</th>
              <th className="px-3 py-2">Product / HSN</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Rate ₹</th>
              <th className="px-3 py-2 text-right">Disc %</th>
              <th className="px-3 py-2 text-right">Tax %</th>
              <th className="px-3 py-2 text-right">Taxable</th>
              <th className="px-3 py-2 text-right">Tax</th>
              <th className="px-3 py-2 text-right">Total</th>
              {!readOnly && <th className="px-3 py-2 w-8"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {form.lines.length === 0 && (
              <tr><td colSpan={readOnly ? 9 : 10} className="px-3 py-8 text-center text-gray-400">No lines yet. {form.warehouse_id ? "Search & pick from warehouse stock above." : "Choose a warehouse first."}</td></tr>
            )}
            {form.lines.map((ln, i) => {
              const s = Number(ln.qty || 0) * Number(ln.rate || 0);
              const ld = s * (Number(ln.discount_pct || 0) / 100);
              const nb = s - ld;
              // Share of invoice discount (client-side estimate for preview)
              const totalNet = form.lines.reduce((sum, x) => sum + (Number(x.qty || 0) * Number(x.rate || 0)) * (1 - Number(x.discount_pct || 0) / 100), 0);
              const invDiscTot = totalNet * (Number(form.invoice_discount_pct || 0) / 100) + Number(form.invoice_discount_amount || 0);
              const share = totalNet > 0 ? nb / totalNet : 0;
              const tx = nb - (Math.min(invDiscTot, totalNet) * share);
              const tax = tx * (Number(ln.tax_pct || 0) / 100);
              return (
                <tr key={ln.product_id} data-testid={`inv-line-${ln.product_id}`}>
                  <td className="px-3 py-2 text-xs text-gray-500">{i + 1}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{ln.product_name}</div>
                    <div className="text-[11px] text-gray-500 font-mono">{ln.product_sku || "—"}</div>
                    <input
                      value={ln.hsn_code || ""}
                      onChange={(e) => setLine(i, { hsn_code: e.target.value })}
                      className="mt-1 h-6 w-24 rounded border border-gray-200 px-2 text-[11px] font-mono"
                      placeholder="HSN"
                      disabled={readOnly}
                      data-testid={`inv-line-hsn-${ln.product_id}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Input type="number" step="0.001" min="0" value={ln.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="h-8 w-20 text-right tabular-nums font-mono ml-auto" disabled={readOnly} data-testid={`inv-line-qty-${ln.product_id}`} />
                    {ln._avail_qty != null && <div className="text-[10px] text-gray-400 mt-0.5">avail {ln._avail_qty}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Input type="number" step="0.01" min="0" value={ln.rate} onChange={(e) => setLine(i, { rate: e.target.value })} className="h-8 w-24 text-right tabular-nums font-mono ml-auto" disabled={readOnly} data-testid={`inv-line-rate-${ln.product_id}`} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Input type="number" step="0.01" min="0" max="100" value={ln.discount_pct} onChange={(e) => setLine(i, { discount_pct: e.target.value })} className="h-8 w-16 text-right tabular-nums font-mono ml-auto" disabled={readOnly} data-testid={`inv-line-disc-${ln.product_id}`} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Input type="number" step="0.01" min="0" max="100" value={ln.tax_pct} onChange={(e) => setLine(i, { tax_pct: e.target.value })} className="h-8 w-16 text-right tabular-nums font-mono ml-auto" disabled={readOnly} data-testid={`inv-line-tax-${ln.product_id}`} />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(tx)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(tax)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatINR(tx + tax)}</td>
                  {!readOnly && (
                    <td className="px-3 py-2 text-right">
                      <button className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center" onClick={() => removeLine(i)} data-testid={`inv-line-del-${ln.product_id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Surface>

      <div className="grid grid-cols-3 gap-4">
        <Surface className="col-span-2">
          <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Invoice Discounts</h3>
          <div className="grid grid-cols-2 gap-4">
            <F label="Discount %">
              <Input type="number" step="0.01" min="0" max="100" value={form.invoice_discount_pct} onChange={(e) => set("invoice_discount_pct", e.target.value)} disabled={readOnly} data-testid="inv-disc-pct" />
            </F>
            <F label="Discount Amount (₹)">
              <Input type="number" step="0.01" min="0" value={form.invoice_discount_amount} onChange={(e) => set("invoice_discount_amount", e.target.value)} disabled={readOnly} data-testid="inv-disc-amt" />
            </F>
          </div>
          <div className="mt-3 text-xs text-gray-500 flex items-start gap-1.5"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /> Invoice-level discount is apportioned across lines by taxable share so GST is computed on the net amount.</div>
        </Surface>
        <Surface>
          <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Totals ({isIntrastate ? "Intrastate" : "Interstate"})</h3>
          <div className="space-y-1.5 text-sm">
            <Row label="Subtotal" value={t.subtotal} />
            <Row label="Line Discounts" value={-Number(t.line_discount_total || 0)} negative />
            <Row label="Invoice Discount" value={-Number(t.invoice_discount || 0)} negative />
            <Row label="Taxable" value={t.taxable_amount} bold />
            {isIntrastate ? (
              <>
                <Row label="CGST" value={t.cgst_total} />
                <Row label="SGST" value={t.sgst_total} />
              </>
            ) : (
              <Row label="IGST" value={t.igst_total} />
            )}
            <Row label="Round-off" value={t.roundoff || 0} tone="text-gray-500" />
            <div className="mt-3 pt-2 border-t border-gray-200 flex justify-between items-baseline">
              <span className="font-heading font-bold text-gray-900">Grand Total</span>
              <span className="font-heading font-bold text-lg text-[#9B1C1C] tabular-nums" data-testid="inv-grand-total">{formatINR(t.grand_total)}</span>
            </div>
          </div>
        </Surface>
      </div>
    </div>
  );
}

function Row({ label, value, negative = false, bold = false, tone = "" }) {
  return (
    <div className="flex justify-between text-xs">
      <span className={`${bold ? "font-semibold" : ""} ${tone}`}>{label}</span>
      <span className={`tabular-nums ${bold ? "font-semibold" : ""} ${negative && value ? "text-red-600" : ""} ${tone}`}>{formatINR(Number(value || 0))}</span>
    </div>
  );
}
